import React, { useContext, useEffect, useState, useRef } from 'react';
import { AppContext } from '~/providers/AppProvider';
import SEO from '~/components/seo';
import Layout from '~/components/layout';
import styled from 'styled-components';
import { getHomePageUrl } from '~/utils/routing';
import { useRouter } from 'next/router';
import useWindowSize from '~/hooks/useWindowSize';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import Link from 'next/link';

import dynamic from 'next/dynamic';
import ContactForm from '~/components/contact/contactForm';
import getConfig from 'next/config';
const Team = dynamic(async () => await import('~/components/contact/team'));
const { publicRuntimeConfig } = getConfig();

export default function ContactPage({}) {
  const appCtx = useContext(AppContext);
  const router = useRouter();
  const contactRef = useRef(null);
  const [formHeight, setFormHeight] = useState(1000);
  const [formSent, setFormSent] = useState(false);
  const size = useWindowSize();

  const phoneNumber = '+1 (954) 715-4114';

  useEffect(() => {
    setFormHeight(contactRef?.current?.offsetHeight);
  }, [size, formSent]);

  return (
    <Layout>
      <SEO title={t`seo.contact.title`} description={t`seo.contact.description`} />
      <ContactSection>
        <HeroImage background="//s3.amazonaws.com/boatbound_development/comfy/cms/files/files/000/000/024/original/luxury-yacht-hero-desktop_2.jpg" />
        <HeroImageMobile background="//s3.amazonaws.com/boatbound_development/comfy/cms/files/files/000/000/025/original/luxury-yacht-hero-mobile_2.jpg" />

        <HeroContent>
          <Header>
            <Link href={`${getHomePageUrl(appCtx)}`} passHref={true}>
              <a>
                <Logo
                  alt="logo"
                  height="32"
                  src={`${publicRuntimeConfig.ASSETS_PREFIX}/images/bs-logo-white.svg`}
                ></Logo>
              </a>
            </Link>

            {/* <CallUs href={`tel:${phoneNumber}`}> */}
            {/*    <Trans id="contact.call" /> {phoneNumber} */}
            {/* </CallUs> */}
          </Header>
          <HeroContentWrapper>
            <Left>
              <div>
                <Heading>
                  <Trans id="contact.luxuryYachts" />
                </Heading>
                <br></br>
                <SubHeading>
                  <Trans id="contact.chartersAvailable" />
                </SubHeading>
              </div>
            </Left>

            <Right>
              <ContactForm fwRef={contactRef} formSent={formSent} setFormSent={setFormSent} />
            </Right>
          </HeroContentWrapper>
        </HeroContent>
      </ContactSection>
      <ReasonsSection height={formHeight / 2}>
        <ContentWrapper>
          <Description>
            <Trans id="contact.description" />
          </Description>
        </ContentWrapper>
      </ReasonsSection>
      <Team />
    </Layout>
  );
}

const ContactSection = styled.div`
  position: relative;
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 640px) {
    min-height: 700px;
  }
`;

const ReasonsSection = styled.div`
  height: ${(props) => (props.height ? `${props.height}px` : '500px')};
  min-height: 220px;
  background-color: #17233c;

  @media screen and (max-width: 960px) {
    height: unset;
    padding-top: 30px;
    padding-bottom: 30px;
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 0;
  margin: 0 auto;
  max-width: 1140px;
  padding: 0 1em;
  width: 100%;

  @media only screen and (max-width: 960px) {
    position: unset;
    padding-bottom: 30px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1em;
  padding-bottom: 3em;
`;

const CallUs = styled.a`
  text-decoration: none;
  padding: 0.9em 1.4em;
  text-transform: none;
  font-weight: 500;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  border: solid 1px #fff;
  border-radius: 6px;
  color: #fff;
  background-color: transparent;
  line-height: 1.5em;
  cursor: pointer;
  transition: all 0.5s ease;

  @media (max-width: 640px) {
    padding: 0.3em 0.6em;
    text-align: center;
    line-height: 21px;
  }

  &:hover {
    background: #072bc266;
  }
`;

const Logo = styled.img`
  cursor: pointer;
  margin-right: 32px;
`;

const HeroContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 960px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const Right = styled.div``;

const Heading = styled.h1`
  font-size: 4em;
  line-height: 1;
  margin-bottom: 0;
  color: white;
  font-weight: 600;

  @media (max-width: 640px) {
    font-size: 40px;
    line-height: 48px;
  }
`;

const SubHeading = styled.h3`
  font-size: 1.5em;
  line-height: 1.333333;
  margin-bottom: 1.333333em;
  color: white;
  font-weight: 600;

  @media (max-width: 960px) {
    font-weight: 500;
    font-size: 21px;
    line-height: 28px;
    letter-spacing: -0.02em;
  }
`;

const HeroImage = styled.div`
  left: inherit;
  top: inherit;
  -webkit-transform: none;
  -ms-transform: none;
  transform: none;

  background: url(${(props) => props.background}) center top 100% / cover no-repeat !important;
  width: 100%;
  height: 100%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center left;
  z-index: -999;

  @media only screen and (max-width: 960px) {
    display: none;
  }
`;

const HeroImageMobile = styled.div`
  background-image: url(${(props) => props.background});
  left: inherit;
  top: inherit;
  transform: none;
  background-position: center;
  width: 100%;
  height: 100%;
  position: absolute;
  background-size: cover;
  background-repeat: no-repeat;
  z-index: -999;

  @media only screen and (min-width: 960px) {
    display: none;
  }
`;

const Description = styled.div`
  font-weight: 400;
  font-size: 20px;
  line-height: 30px;
  color: #dbdfe5;
  width: 45%;
  flex-basis: auto;
  flex: 0 0 100%;

  @media only screen and (max-width: 960px) {
    width: 100%;
  }
`;

const ContentWrapper = styled.div`
  margin: 0 auto;
  max-width: 1140px;
  padding: 2.2em;
`;
